/**
 * Annuler un cours, quitter une liste d'attente, révéler son spot : habillage v2 (modale de la
 * maquette, 12-modale-annulation), LOGIQUE ET REQUÊTES DE LA V1 (SPEC VC3) :
 * - annulation = screens/Class/components/CancelVisitModal.tsx onCancelConfirmation() ;
 * - liste d'attente = screens/Account/Visits/WaitlistHistoryItem.tsx handleCancelWaitlist() ;
 * - spot = screens/Account/Visits/VisitItem.tsx handleDiscoverSpot().
 */
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useState } from 'react';
import type { PickaspotClass } from 'types/class';
import type { ClientVisit } from 'types/client';
import { getLateCancelHours, isVisitLateCancel } from 'utils/functions';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { lateCancelWarning } from '../../Class/lib/format';
import { fmtDateShort, fmtDayLong, fmtTime, toDate } from '../lib/format';
import { waitlistStart, type WaitlistEntry } from '../lib/waitlist';
import { AccountModal, ModalButton, ModalLede, ModalNotice } from './Ui';

/**
 * Phrase « Boxing à Louvre, lundi 5 oct. à 07:45. » de la modale ; espaces insécables dans la date et avant l'heure :
 * « lundi 5 oct. » et « à 07:45 » jamais coupés (revue du 05/10).
 */
function describeClass(name: string | undefined, studio: string | undefined, start: Date | null) {
  const parts = [name || 'Cours'];
  if (studio) parts.push(`à ${studio}`);
  const when = start
    ? `, ${fmtDayLong(start)}\u00a0${fmtDateShort(start).replace(/ /g, '\u00a0')} à\u00a0${fmtTime(start)}`
    : '';
  return `${parts.join(' ')}${when}.`;
}

/**
 * Contexte d'un bouton répété dans une liste (« Annuler », « Mon spot ») pour les lecteurs d'écran : « Boxing, lundi
 * 5 oct. à 07:45 » (revue du 05/10 : quatre « Annuler, bouton » sans contexte). Le nom accessible commence par le
 * libellé visible (WCAG 2.5.3).
 */
function visitContext(visit: ClientVisit): string {
  const start = toDate(visit.StartDateTime);
  const when = start ? `, ${fmtDayLong(start)} ${fmtDateShort(start)} à ${fmtTime(start)}` : '';
  return `${visit.Name || 'cours'}${when}`;
}

export function CancelVisitButton({
  visit,
  studioName,
  onCancelled,
  variant = 'ghost',
}: {
  visit: ClientVisit;
  studioName?: string;
  onCancelled?: () => void;
  /** ghost : bouton contour (tableaux) ; quiet : action discrète (lignes de Mon compte). */
  variant?: 'ghost' | 'quiet';
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refetch: refetchVisits } = useListVisits() || {};
  const { refetch: refetchClientServices } = useListClientServices();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isLate = isVisitLateCancel(visit);
  const hours = getLateCancelHours(visit.SiteId);

  const close = () => {
    setIsLoading(false);
    setIsOpen(false);
  };

  // = CancelVisitModal.tsx onCancelConfirmation() + onConfirm()
  async function onConfirm() {
    setIsLoading(true);
    try {
      await HTTP.post(`/class/cancel`, {
        VisitId: visit.Id,
        ClassId: visit.ClassId,
        StartDateTime: visit.StartDateTime,
        SiteId: visit.SiteId,
      }).send();

      onCancelled?.();

      await Promise.all([refetchVisits(), refetchClientServices()]);
      // v2 : l'historique paginé est relu lui aussi.
      queryClient.invalidateQueries({ queryKey: ['/client/visits/history'] });
      toast({ title: 'La réservation a été annulée', status: 'success' });
    } catch (error) {
      const { title, description } = resolveUserError('cancelBooking', error);
      toast({ title, description, status: 'error', isClosable: true });
    } finally {
      setIsLoading(false);
    }
    close();
  }

  // Recette RIISE du 28/10 (décision de Luc) : « Annulation tardive » en rouge laissait croire le cours annulé ; le
  // bouton reste « Annuler », l'avertissement est dans la popin.
  const label = variant === 'quiet' ? 'Annuler' : 'Annuler ce cours';
  const warning = lateCancelWarning(hours);

  return (
    <>
      <button
        type="button"
        className={`mmv2-btn mmv2-btn--sm mmv2-btn--${variant} mmv2-acct-cancel`}
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-label={`${label}\u00a0: ${visitContext(visit)}`}
      >
        {label}
      </button>
      <AccountModal
        isOpen={isOpen}
        onClose={close}
        title="Annuler ma réservation"
        footer={
          <>
            <ModalButton tone="quiet" onClick={close} isDisabled={isLoading}>
              Garder ma réservation
            </ModalButton>
            <ModalButton tone="danger" onClick={onConfirm} isLoading={isLoading} loadingText="Annulation…">
              {isLate ? 'Annuler quand même' : 'Confirmer l’annulation'}
            </ModalButton>
          </>
        }
      >
        <ModalLede>{describeClass(visit.Name, studioName, toDate(visit.StartDateTime))}</ModalLede>
        {isLate ? (
          <ModalNotice tone="warm">
            {/* Même phrase que la modale de la fiche cours (CancelModal) : lateCancelWarning. */}
            <b>{warning.lead}</b> {warning.text}
          </ModalNotice>
        ) : (
          <ModalNotice>Le crédit repart directement dans votre compte.</ModalNotice>
        )}
      </AccountModal>
    </>
  );
}

export function LeaveWaitlistButton({
  entry,
  studioName,
  onLeft,
}: {
  entry: WaitlistEntry;
  studioName?: string;
  onLeft?: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const start = waitlistStart(entry);

  const close = () => {
    setIsLoading(false);
    setIsOpen(false);
  };

  // = WaitlistHistoryItem.tsx handleCancelWaitlist() (via CancelWaitlistModal onConfirm)
  async function onConfirm() {
    setIsLoading(true);
    try {
      await HTTP.delete(`/class/${entry.ClassId}/waitlist`).send();
      queryClient.setQueryData(['/class', entry.ClassId, '/waitlist'], null);
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      onLeft?.();
      toast({ title: "Vous avez quitté la liste d’attente", status: 'success' });
      close();
    } catch (error) {
      const { title, description } = resolveUserError('cancelWaitlist', error);
      toast({ title, description, status: 'error', isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <button type="button" className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm" onClick={() => setIsOpen(true)} aria-haspopup="dialog">
        Quitter la liste d’attente
      </button>
      <AccountModal
        isOpen={isOpen}
        onClose={close}
        title="Quitter la liste d’attente"
        footer={
          <>
            {/* Désactivé pendant l'envoi, comme « Garder ma réservation » (revue du 05/10). */}
            <ModalButton tone="quiet" onClick={close} isDisabled={isLoading}>
              Rester sur la liste
            </ModalButton>
            <ModalButton tone="dark" onClick={onConfirm} isLoading={isLoading} loadingText="Un instant…">
              Quitter la liste
            </ModalButton>
          </>
        }
      >
        <ModalLede>
          Voulez-vous vraiment quitter la liste d’attente de{' '}
          {describeClass(entry.ClassSchedule?.ClassDescription?.Name, studioName, start).replace(/\.$/, '')}&nbsp;?
        </ModalLede>
        <ModalNotice>Aucun crédit n’a été débité, et aucun ne le sera.</ModalNotice>
      </AccountModal>
    </>
  );
}

/** = VisitItem.tsx handleDiscoverSpot() : « Mon spot » révèle le numéro (GET class/{id}/reservation). */
export function SpotReveal({ visit }: { visit: ClientVisit }) {
  const [loading, setLoading] = useState(false);
  const [spot, setSpot] = useState<number | null>(null);
  const [tried, setTried] = useState(false);

  if (!visit.ClassId) return null;
  if (spot) return <span className="mmv2-acct-spot">spot #{spot}</span>;
  if (tried && !loading) return <span className="mmv2-acct-spot mmv2-muted">spot non attribué</span>;

  const reveal = async () => {
    setLoading(true);
    try {
      const response = await HTTP.get<PickaspotClass['reservations'][number]>(`/class/${visit.ClassId}/reservation`, {
        SiteId: visit.SiteId,
        VisitId: visit.Id,
      }).send();
      setSpot(response?.spot?.spotNumber || null);
    } catch (error) {
      // = v1 : erreur silencieuse.
    } finally {
      setLoading(false);
      setTried(true);
    }
  };

  return (
    <button
      type="button"
      className="mmv2-linkish mmv2-acct-spotbtn"
      onClick={reveal}
      disabled={loading}
      aria-busy={loading}
      aria-label={loading ? undefined : `Mon spot\u00a0: ${visitContext(visit)}`}
    >
      {loading ? 'Recherche…' : 'Mon spot'}
    </button>
  );
}
